import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox, CollapsibleBlock, PinAction, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { useManageSitesOptions } from '@hooks/useManageSitesOptions';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { resetRightSideControls, setPinnedFilter, setSitesFilters, setSitesFilterSectionsState } from '@slices/map';

import { isEmpty, sortObjectByKey } from '@utils/common';
import { getTechnologyColor } from '@utils/getTechnologyColor';

import { ISiteType } from '@interfaces/cells/siteProfile';
import { ITechnologyCheckbox, TTechnology } from '@interfaces/map/map';

import {
	EControlsType,
	ESitesFilterSections,
	TEControlsType,
	TSitesFilterSections,
	TTechnologiesValues,
} from '@enums/common';

import TechnologyCheckboxes from './TechnologyCheckboxes';

import styles from './Sites.module.scss';

const Sites = () => {
	const dispatch = useAppDispatch();
	const {
		appliedSitesFiltersValues,
		pinnedFilter,
		sitesFilterSectionsState,
	} = useAppSelector(map);

	const {
		technologies: isTechnologiesOpen,
		siteTypes: isSiteTypesOpen,
	} = sitesFilterSectionsState;

	const { siteTypes, technologies, isLoadingTechnologies } = useManageSitesOptions();

	const [siteTypeItems, setSiteTypeItems] = useState<Array<ISiteType>>([]);
	const [technologyItems, setTechnologyItems] = useState<Array<ITechnologyCheckbox>>([]);

	const [isPinned, setIsPinned] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.filters : null);
	}, [isPinned, pinFilter]);

	useEffect(() => {
		if (!isEmpty(siteTypes)) {
			setSiteTypeItems(sortObjectByKey((siteTypes as Array<ISiteType>).map((item) => {
				return {
					...item,
					isSelected: true,
				};
			}), 'title'));
		}
	}, [siteTypes]);

	useEffect(() => {
		if (!isEmpty(technologies)) {
			const technologyItemsToSort = (technologies as Array<TTechnology>).map((item) => {
				const color = getTechnologyColor(item.name);

				return {
					...item,
					isSelected: true,
					color,
				};
			});
			const technologyItems = sortObjectByKey(technologyItemsToSort, 'title');

			setTechnologyItems(technologyItems);
		}
	}, [dispatch, technologies]);

	useEffect(() => {
		if (!isEmpty(appliedSitesFiltersValues)) {
			const { technologies: appliedTechnologies, siteType: appliedSiteTypes } = appliedSitesFiltersValues;
			const isAllTechnologiesSelected = appliedTechnologies?.length === technologyItems?.length
				|| isEmpty(appliedTechnologies);
			const isAllSiteTypesSelected = appliedSiteTypes?.length === siteTypeItems?.length
				|| isEmpty(appliedSiteTypes);

			setTechnologyItems((prev) => {
				return prev.map((item) => {
					return {
						...item,
						isSelected: isAllTechnologiesSelected || !!appliedTechnologies?.includes(item.name),
					};
				});
			});

			setSiteTypeItems((prev) => {
				return prev.map((item) => {
					const selectedSiteType = appliedSiteTypes?.some((siteType) => siteType === item.title);

					return {
						...item,
						isSelected: isAllSiteTypesSelected || selectedSiteType,
					};
				});
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appliedSitesFiltersValues]);

	const applySitesFilters = useCallback(() => {
		const technologies = technologyItems.reduce<Array<TTechnologiesValues>>((acc, item) => {
			if (item.isSelected) {
				acc.push(item.name);
			}

			return acc;
		}, []);

		const siteType = siteTypeItems.reduce<Array<string>>((acc, item) => {
			if (item.isSelected) {
				acc.push(item.title);
			}

			return acc;
		}, []);

		dispatch(setSitesFilters({
			technologies: technologies.length === technologyItems.length ? [] : technologies,
			siteType: siteType.length === siteTypeItems.length ? [] : siteType,
		}));

		if (!pinnedFilter) dispatch(resetRightSideControls());
	}, [
		dispatch,
		pinnedFilter,
		siteTypeItems,
		technologyItems,
	]);

	const handleReset = useCallback(() => {
		setTechnologyItems((prev) => {
			return prev.map((item) => {
				return {
					...item,
					isSelected: true,
				};
			});
		});
		setSiteTypeItems((prev) => {
			return prev.map((item) => {
				return {
					...item,
					isSelected: true,
				};
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	const toggleSectionsState = useCallback((name: string) => {
		dispatch(setSitesFilterSectionsState({
			...sitesFilterSectionsState,
			[name]: !sitesFilterSectionsState[name as TSitesFilterSections],
		}));
	}, [dispatch, sitesFilterSectionsState]);

	const handleChangeSiteType = useCallback((id: string) => () => {
		setSiteTypeItems((prev) => {
			return prev.map((item) => {
				if (item.id === id) {
					return {
						...item,
						isSelected: !item.isSelected,
					};
				}

				return item;
			});
		});
	}, [setSiteTypeItems]);

	const handleChangeTechnology = useCallback((name: string) => () => {
		setTechnologyItems((prev) => {
			return prev.map((item) => {
				if (item.name === name) {
					return {
						...item,
						isSelected: !item.isSelected,
					};
				}

				return item;
			});
		});
	}, [setTechnologyItems]);

	const isDirtyTechs = useMemo(() => {
		if (appliedSitesFiltersValues.technologies?.length === 0) {
			return !technologyItems.every(item => item.isSelected)
				&& !siteTypeItems.every(item => !item.isSelected);
		}

		return technologyItems.some(item => {
			if (item.isSelected) {
				return !appliedSitesFiltersValues.technologies?.includes(item.name);
			}

			return appliedSitesFiltersValues.technologies?.includes(item.name);
		});
	}, [
		appliedSitesFiltersValues.technologies,
		siteTypeItems,
		technologyItems,
	]);

	const isDirtyTypes = useMemo(() => {
		if (appliedSitesFiltersValues.siteType?.length === 0) {
			return !siteTypeItems.every(item => item.isSelected)
				&& !siteTypeItems.every(item => !item.isSelected);
		}

		return siteTypeItems.some(item => {
			if (item.isSelected) {
				return !appliedSitesFiltersValues.siteType?.includes(item.title);
			}

			return appliedSitesFiltersValues.siteType?.includes(item.title);
		});
	}, [
		appliedSitesFiltersValues.siteType,
		siteTypeItems,
	]);

	const isDirtyForm = isDirtyTechs || isDirtyTypes;

	const isApplyButtonAvailable = useMemo(() => {
		return (!isEmpty(technologyItems.filter(item => item.isSelected))
				|| !isEmpty(siteTypeItems.filter(item => item.isSelected)))
			&& isDirtyForm;
	}, [isDirtyForm, siteTypeItems, technologyItems]);

	return (
		<div
			ref={containerRef}
			className={styles.filtersContainer}
		>
			{isLoadingTechnologies && <SpinnerOverlay />}
			<div className={styles.header}>
				<div className={styles.sectionTitle}>Sites filters</div>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			<div className={styles.sectionContainer}>
				<CollapsibleBlock
					collapsibleBlockClassname={styles.collapsibleBlock}
					isOpen={isTechnologiesOpen}
					label={<div className={styles.sectionTitle}>Technologies</div>}
					name={ESitesFilterSections.technologies}
					toggleState={toggleSectionsState}
				>
					<TechnologyCheckboxes
						groupName={ESitesFilterSections.technologies}
						onChangeTechnology={handleChangeTechnology}
						technologies={technologyItems}
					/>
				</CollapsibleBlock>
			</div>
			<div className={styles.sectionContainer}>
				<CollapsibleBlock
					collapsibleBlockClassname={styles.collapsibleBlock}
					isOpen={isSiteTypesOpen}
					label={<div className={styles.sectionTitle}>Site type / Civil model</div>}
					name={ESitesFilterSections.siteTypes}
					toggleState={toggleSectionsState}
				>
					<div className={styles.siteTypesContainer}>
						{!!siteTypeItems.length && siteTypeItems.map(({ title, id, isSelected }) => {
							return (
								<Checkbox
									key={id}
									checked={isSelected}
									label={title}
									name={id}
									onChange={handleChangeSiteType(id)}
								/>
							);
						})}
					</div>
				</CollapsibleBlock>
			</div>
			<SubmissionButtons
				className={styles.submissionButtonsContainer}
				disabledPositive={!isApplyButtonAvailable}
				negativeHandler={handleReset}
				negativeLabel='Reset all'
				positiveHandler={applySitesFilters}
				positiveLabel='Apply'
			/>
		</div>
	);
};

export default memo(Sites);
